import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, BookOpen, Clock, Target, Award, CheckCircle, XCircle, Eye, EyeOff, Brain } from 'lucide-react';
import { AVAILABLE_YEARS, getPaperByYear } from '../data/pastPapersData';
import deepSeekDrillApiService from '../services/deepseekDrillApi';
import MathExpression from './MathExpression';
import DrillQuestions from './DrillQuestions';
import DrillProgress from './DrillProgress';

const PastPaperViewer = () => {
  const [selectedYear, setSelectedYear] = useState(2018);
  const [currentPaper, setCurrentPaper] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showSolution, setShowSolution] = useState({});
  const [filterTopic, setFilterTopic] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'practice'
  const [userAnswers, setUserAnswers] = useState({});
  const [drillData, setDrillData] = useState({});
  const [showDrill, setShowDrill] = useState({});
  const [isGeneratingDrill, setIsGeneratingDrill] = useState({});
  const [drillProgress, setDrillProgress] = useState({});

  // Load paper when year changes
  useEffect(() => {
    const paper = getPaperByYear(selectedYear);
    setCurrentPaper(paper);
    setSelectedQuestion(null);
    setShowSolution({});
    setUserAnswers({});
    setDrillData({});
    setShowDrill({});
    setIsGeneratingDrill({});
    setDrillProgress({});
  }, [selectedYear]);

  // Filter questions based on topic and difficulty
  const filteredQuestions = currentPaper?.questions?.filter(question => {
    const topicMatch = filterTopic === 'all' || question.topic === filterTopic;
    const difficultyMatch = filterDifficulty === 'all' || question.difficulty === filterDifficulty;
    return topicMatch && difficultyMatch;
  }) || [];

  const toggleSolution = (questionId) => {
    setShowSolution(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const handleAnswerSelect = (questionId, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const getAnswerStatus = (questionId, selectedAnswer) => {
    const question = currentPaper?.questions?.find(q => q.id === questionId);
    if (!question || !selectedAnswer) return null;
    return selectedAnswer === question.correctAnswer ? 'correct' : 'incorrect';
  };

  const calculateScore = () => {
    const totalAnswered = Object.keys(userAnswers).length;
    const correctAnswers = Object.entries(userAnswers).filter(([questionId, answer]) => {
      const question = currentPaper?.questions?.find(q => q.id === questionId);
      return question && answer === question.correctAnswer;
    }).length;
    return { correct: correctAnswers, total: totalAnswered, percentage: totalAnswered > 0 ? Math.round((correctAnswers / totalAnswered) * 100) : 0 };
  };

  // AI Drill functionality with progress tracking
  const handleGenerateDrill = async (question) => {
    const startTime = Date.now();
    setIsGeneratingDrill(prev => ({ ...prev, [question.id]: true }));
    setDrillProgress(prev => ({ 
      ...prev, 
      [question.id]: {
        isGenerating: true,
        startTime: startTime,
        tokensUsed: 0,
        estimatedTokens: 0
      }
    }));
    
    try {
      // Use real AI generation with DeepSeek API
      const drillData = await deepSeekDrillApiService.generateDrillQuestions(
        question, 
        5, // Number of drills
        (progress) => {
          // Update progress for this specific question
          setDrillProgress(prev => ({
            ...prev,
            [question.id]: {
              ...prev[question.id],
              ...progress
            }
          }));
        }
      );
      
      setDrillData(prev => ({ ...prev, [question.id]: drillData }));
      setShowDrill(prev => ({ ...prev, [question.id]: true }));
    } catch (error) {
      console.error('Error generating drill questions:', error);
      // Show error message to user instead of falling back to mock data
      alert('生成練習題時發生錯誤，請稍後再試。錯誤詳情：' + error.message);
    } finally {
      setIsGeneratingDrill(prev => ({ ...prev, [question.id]: false }));
      // Clear progress after completion
      setTimeout(() => {
        setDrillProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[question.id];
          return newProgress;
        });
      }, 2000); // Keep progress visible for 2 seconds after completion
    }
  };

  const handleDrillComplete = (questionId, results) => {
    console.log(`Drill completed for question ${questionId}:`, results);
  };

  const handleGenerateNewDrill = async (question) => {
    // Clear existing drill data
    setDrillData(prev => {
      const newData = { ...prev };
      delete newData[question.id];
      return newData;
    });
    
    // Generate new drill
    await handleGenerateDrill(question);
  };

  const toggleDrill = (questionId) => {
    setShowDrill(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  if (!currentPaper) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">載入中...</p>
        </div>
      </div>
    );
  }

  const score = calculateScore();

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">HKDSE 數學科歷屆試題</h1>
            <p className="text-gray-600">選擇年份查看完整試卷及詳細解答</p>
          </div>
          
          {/* Year Selection */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">年份:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {AVAILABLE_YEARS.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">模式:</label>
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="list">瀏覽模式</option>
                <option value="practice">練習模式</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Paper Info */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-center space-x-3">
            <BookOpen className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-sm text-gray-600">試卷</p>
              <p className="font-semibold">{currentPaper?.title || '載入中...'}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Target className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-sm text-gray-600">題目數量</p>
              <p className="font-semibold">{currentPaper?.totalQuestions || 0} 題</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Clock className="w-8 h-8 text-orange-500" />
            <div>
              <p className="text-sm text-gray-600">考試時間</p>
              <p className="font-semibold">{currentPaper?.timeLimit || 0} 分鐘</p>
            </div>
          </div>
          
          {viewMode === 'practice' && (
            <div className="flex items-center space-x-3">
              <Award className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-sm text-gray-600">目前分數</p>
                <p className="font-semibold">{score.correct}/{score.total} ({score.percentage}%)</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">主題:</label>
            <select
              value={filterTopic}
              onChange={(e) => setFilterTopic(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">全部主題</option>
              {currentPaper.topics?.map(topic => (
                <option key={topic} value={topic}>{topic}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">難度:</label>
            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">全部難度</option>
              <option value="基礎">基礎</option>
              <option value="中級">中級</option>
              <option value="高級">高級</option>
            </select>
          </div>
          
          <div className="text-sm text-gray-600 flex items-center">
            顯示 {filteredQuestions.length} / {currentPaper?.totalQuestions || 0} 題
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-6">
        {filteredQuestions.map((question, index) => {
          const userAnswer = userAnswers[question.id];
          const answerStatus = getAnswerStatus(question.id, userAnswer);
          const hasDrillData = drillData[question.id];
          const isDrillVisible = showDrill[question.id];
          const isGenerating = isGeneratingDrill[question.id];
          const progress = drillProgress[question.id];
          
          return (
            <div key={question.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Question Header */}
              <div className="bg-gray-50 px-6 py-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-lg font-bold text-blue-600">第 {question?.questionNumber || '?'} 題</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      question?.difficulty === '基礎' ? 'bg-green-100 text-green-800' :
                      question?.difficulty === '中級' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {question?.difficulty || '未知'}
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      {question?.topic || '未知'}
                    </span>
                    {viewMode === 'practice' && userAnswer && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${
                        answerStatus === 'correct' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {answerStatus === 'correct' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        <span>{answerStatus === 'correct' ? '正確' : '錯誤'}</span>
                      </span>
                    )}
                    {hasDrillData && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium flex items-center space-x-1">
                        <Brain className="w-3 h-3" />
                        <span>AI練習</span>
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* AI Drill Button */}
                    <button
                      onClick={() => handleGenerateDrill(question)}
                      disabled={isGenerating}
                      className={`flex items-center space-x-2 px-3 py-1 text-sm rounded-lg transition-colors ${
                        isGenerating
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : hasDrillData
                            ? 'bg-purple-500 text-white hover:bg-purple-600'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      {isGenerating ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>生成中...</span>
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 h-4" />
                          <span>{hasDrillData ? '重新生成' : 'AI練習'}</span>
                        </>
                      )}
                    </button>
                    
                    {/* Toggle Drill Button */}
                    {hasDrillData && (
                      <button
                        onClick={() => toggleDrill(question.id)}
                        className="flex items-center space-x-2 px-3 py-1 text-sm text-purple-600 hover:text-purple-800 transition-colors"
                      >
                        {isDrillVisible ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        <span>{isDrillVisible ? '隱藏練習' : '顯示練習'}</span>
                      </button>
                    )}
                    
                    {/* Solution Toggle Button */}
                    <button
                      onClick={() => toggleSolution(question.id)}
                      className="flex items-center space-x-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      {showSolution[question.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      <span>{showSolution[question.id] ? '隱藏解答' : '顯示解答'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Question Content */}
              <div className="p-6">
                {/* Question Text */}
                <div className="mb-6">
                  <div className="text-lg mb-4">
                    <MathExpression>{question?.question || '題目載入中...'}</MathExpression>
                  </div>
                  
                  {question?.hasImage && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800 mb-2">📊 圖像描述:</p>
                      <p className="text-sm text-blue-700">{question?.imageDescription || ''}</p>
                    </div>
                  )}
                </div>

                {/* Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {question?.options?.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => viewMode === 'practice' && handleAnswerSelect(question.id, option.id)}
                      disabled={viewMode !== 'practice'}
                      className={`p-4 text-left rounded-lg border-2 transition-all ${
                        viewMode === 'practice' 
                          ? userAnswer === option.id
                            ? answerStatus === 'correct'
                              ? 'border-green-500 bg-green-50'
                              : 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                          : 'border-gray-200'
                      } ${
                        showSolution[question.id] && option.id === question?.correctAnswer
                          ? 'border-green-500 bg-green-50'
                          : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          showSolution[question.id] && option.id === question?.correctAnswer
                            ? 'bg-green-500 text-white'
                            : viewMode === 'practice' && userAnswer === option.id
                              ? answerStatus === 'correct'
                                ? 'bg-green-500 text-white'
                                : 'bg-red-500 text-white'
                              : 'bg-gray-100 text-gray-700'
                        }`}>
                          {option.id}
                        </span>
                        <div className="flex-1">
                          <MathExpression>{option?.text || ''}</MathExpression>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Solution */}
                {showSolution[question.id] && (
                  <div className="mt-6 p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      詳細解答 (正確答案: {question?.correctAnswer || '?'})
                    </h4>
                    <div className="text-green-700">
                      <MathExpression>{question?.solution || '解答載入中...'}</MathExpression>
                    </div>
                  </div>
                )}

                {/* AI Drill Section */}
                {isDrillVisible && hasDrillData && (
                  <div className="mt-6 border-t border-gray-200 pt-6">
                    <DrillQuestions
                      drillData={hasDrillData}
                      originalQuestionId={question.id}
                      onDrillComplete={(results) => handleDrillComplete(question.id, results)}
                      onGenerateNewDrill={() => handleGenerateNewDrill(question)}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Statistics */}
      {viewMode === 'practice' && score.total > 0 && (
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">練習統計</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{score.total}</div>
              <div className="text-gray-600">已答題目</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{score.correct}</div>
              <div className="text-gray-600">答對題目</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">{score.percentage}%</div>
              <div className="text-gray-600">正確率</div>
            </div>
          </div>
          
          {score.percentage >= 80 && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg">
              <p className="text-green-800 text-center">🎉 優秀表現！你已經很好地掌握了這些題目。</p>
            </div>
          )}
          {score.percentage >= 60 && score.percentage < 80 && (
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
              <p className="text-yellow-800 text-center">👍 不錯的表現！建議再多練習幾次來加強記憶。</p>
            </div>
          )}
          {score.percentage < 60 && (
            <div className="mt-4 p-4 bg-red-50 rounded-lg">
              <p className="text-red-800 text-center">💪 需要更多練習！建議先複習相關概念再進行練習。</p>
            </div>
          )}
        </div>
      )}

      {/* Drill Progress Modal */}
      {Object.values(drillProgress).some(progress => progress.isGenerating) && (
        <DrillProgress
          isGenerating={true}
          startTime={Object.values(drillProgress).find(p => p.isGenerating)?.startTime}
          tokensUsed={Object.values(drillProgress).find(p => p.isGenerating)?.tokensUsed}
          estimatedTokens={Object.values(drillProgress).find(p => p.isGenerating)?.estimatedTokens}
          costIncurred={Object.values(drillProgress).find(p => p.isGenerating)?.costIncurred}
          stage={Object.values(drillProgress).find(p => p.isGenerating)?.stage}
          message={Object.values(drillProgress).find(p => p.isGenerating)?.message}
        />
      )}
    </div>
  );
};

export default PastPaperViewer; 